/**
 * Build ID fetching utilities
 */

export const buildIdFetcher = {
  /**
   * Fetch the latest build ID from server
   */
  async fetchLatestBuildId(): Promise<string> {
    const isDev = import.meta.env.DEV;
    const timestamp = Date.now(); // Add timestamp to prevent caching

    try {
      if (isDev) {
        // Development environment: fetch from dev endpoint
        const response = await fetch(`/dev-build-id?_t=${timestamp}`, {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
          // Add timeout handling
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch dev build ID: ${response.status} ${response.statusText}`,
          );
        }

        const buildId = await response.text();
        return buildId.trim();
      } else {
        // Production environment: fetch from build-id.txt
        const response = await fetch(`/build-id.txt?_t=${timestamp}`, {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
          // Add timeout handling
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch build ID: ${response.status} ${response.statusText}`);
        }

        const buildId = await response.text();
        return buildId.trim();
      }
    } catch (error) {
      // Enhanced error handling
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error("Fetch build ID timed out after 5 seconds");
      }

      // Re-throw the original error
      throw error;
    }
  },

  /**
   * Check if build-id.txt exists using HEAD request
   * This can be used to verify correct deployment
   */
  async checkBuildIdFileExists(): Promise<boolean> {
    const isDev = import.meta.env.DEV;
    const path = isDev ? "/dev-build-id" : "/build-id.txt";

    try {
      const response = await fetch(path, {
        method: "HEAD",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      return response.ok;
    } catch (error) {
      console.error(`Failed to check if build ID file exists at ${path}:`, error);
      return false;
    }
  },
};
